import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip, Box } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListWarehouseAllocationQuery } from "store/services/warehouseAllocationApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import ExportWarehouseAllocationExcel from "./warehouseAllocationComponents/ExportWarehouseAllocationExcel";
import { BaseTabs } from "components/Shared/BaseTabs";
const getStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
    CONFIRMED: { backgroundColor: "#FEF9E7", textColor: "#F1C40F" },
    FULFILLED: { backgroundColor: "#EBF5FB", textColor: "green" },
  };

  return styles[status?.toUpperCase()] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const WarehouseAllocationListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Warehouse Allocations" }];

  const allocationListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Allocation ID",
      selector: (row) => row.allocation_id,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Link
            title={row?.allocation_id}
            to={`/app/crm/warehouse-allocation/${row?._id}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.allocation_id ? (
              <HighlightedText text={row?.allocation_id} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        </Box>
      ),
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentrequest?.shipmentId,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          {row.shipmentrequest?.shipmentId || "N/A"}
        </Box>
      ),
    },
    {
      name: "Warehouse",
      selector: (row) => row.warehouse?.name,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Link
            title={row?.warehouse?.name}
            to={`/app/crm/warehouse/${row?.warehouse_id}/detail`}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {row?.warehouse?.name ? (
              <HighlightedText text={row?.warehouse?.name} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        </Box>
      ),
    },

    {
      name: "Allocation Time",
      selector: (row) => <AppTableDateTime date={row?.allocation_time} />,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <AppTableDateTime date={row?.allocation_time} />
        </Box>
      ),
    },
    {
      name: "Allocated Capacity",
      selector: (row) => row.allocated_capacity,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          {row?.allocated_capacity ? `${row?.allocated_capacity} units` : "N/A"}
        </Box>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            style={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              fontSize: "0.7rem",
              height: "20px",
              padding: "0 6px",
              borderRadius: "12px",
              width: "auto",
            }}
          />
        </Box>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Link to={`/app/crm/warehouse-allocation/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Allocation" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/warehouse-allocation/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Allocation">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];


  // RTK Query
  const [fetchAllocations, { data: allocationRes, isLoading, isFetching, error }] =
    useLazyListWarehouseAllocationQuery();

  React.useEffect(() => {
    fetchAllocations({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchAllocations, keyword, perPage]);

  useAppMeta(`${activeTab} Warehouse Allocations`);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={<PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={allocationListTabs} activeTab={activeTab} actions={<ExportWarehouseAllocationExcel />} />

        <TableFilter
          searchLabel="Search Allocations"
          disabled={isLoading || isFetching}
        >
          <div className="font-medium"> {activeTab} Allocations </div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={allocationRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={allocationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default WarehouseAllocationListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/warehouse-allocation/create">
      <Button startIcon={<Add />} variant="contained">
        New Allocation
      </Button>
    </Link>
  );
};
